import { useNavigate } from 'react-router-dom';
import { Bell, CalendarDays, Palmtree, Video } from 'lucide-react';
import type { Task } from '../../lib/tasksApi';
import type { Reminder } from '../../lib/remindersApi';
import type { VideoMeetingSummary } from '../../lib/meetApi';
import type { CalendarEvent } from '../../lib/calendarApi';
import type { LeaveDay } from '../../lib/leaveApi';
import { LEAVE_TYPE_COLOR, formatLeaveRange, leaveTypeLabel } from '../../lib/leaveConstants';
import { formatEstimate } from '../../lib/estimate';
import { formatEstimate as formatReminderEstimate } from '../../lib/reminderDates';
import { statusBadgeStyle } from '../tasks/taskConstants';
import { HoverCard, TaskHoverCard } from '../tasks/TaskHoverCard';
import { PRIORITY_LABEL, PriorityIcon } from '../tasks/linear/taskVisuals';
import { taskPath } from '../../lib/taskLink';
import { Avatar } from '../tasks/linear/Avatar';
import { useI18n } from '../../contexts/I18nContext';
import {
  CalendarEventPreviewCard,
  MeetingPreviewCard,
  REMINDER_COLOR,
  ReminderPreviewCard,
  calendarEventColor,
  calendarEventMinutes,
  meetingColor,
  meetingStatusLabel,
} from './previewCards';

/**
 * The chips every calendar layout is built from.
 *
 * There are five of them now — month grid, month day panel, week grid, week
 * agenda and (via the same components) their mobile variants — so the chips
 * live here rather than inside whichever view happened to need them first.
 */

/**
 * Every chip shares one shape — the task chip's: a dimmed first line saying
 * what kind of thing it is, then the title with its duration. Only the tint
 * tells the species apart: a task wears its status colour, a reminder is
 * red (a deadline, like the strip at the top), a meeting is sky blue and an
 * absence wears the colour of its leave type.
 */
function chipClass(extra = ''): string {
  return `block w-full rounded px-1.5 py-1 text-left text-xs font-medium hover:opacity-80 ${extra}`;
}

export function ReminderChip({ reminder, locale }: { reminder: Reminder; locale: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(reminder.dueAt),
  );
  return (
    <HoverCard content={<ReminderPreviewCard reminder={reminder} />}>
      <button
        type="button"
        onClick={() => navigate('/reminders')}
        className={chipClass()}
        style={statusBadgeStyle(REMINDER_COLOR)}
      >
        <span className="flex items-center gap-1 text-[10px] font-normal leading-tight opacity-75">
          <Bell size={10} className="shrink-0" />
          <span className="min-w-0 truncate">{t('reminders.title')}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{time}</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="min-w-0 flex-1 truncate">{reminder.title}</span>
          {reminder.estimatedMinutes != null && (
            <span className="shrink-0 opacity-70">
              {formatReminderEstimate(reminder.estimatedMinutes)}
            </span>
          )}
        </span>
      </button>
    </HoverCard>
  );
}

/**
 * An event from the person's Google Calendar (People → Integrări). It lives in
 * Google, so there is nothing of ours to open: the chip only previews on hover.
 * Painted as Google paints it (see previewCards).
 */
export function CalendarEventChip({ event, locale }: { event: CalendarEvent; locale: string }) {
  const { t } = useI18n();
  const time = event.allDay
    ? t('calendar.allDay')
    : new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
        new Date(event.startTime),
      );
  const cancelled = event.status === 'CANCELLED';
  return (
    <HoverCard content={<CalendarEventPreviewCard event={event} />}>
      <div
        className={chipClass(`cursor-default ${cancelled ? 'line-through' : ''}`)}
        style={statusBadgeStyle(calendarEventColor(event))}
      >
        <span className="flex items-center gap-1 text-[10px] font-normal leading-tight opacity-75">
          <CalendarDays size={10} className="shrink-0" />
          <span className="min-w-0 truncate">{t('calendar.googleEvent')}</span>
          <span className="shrink-0">·</span>
          <span className="shrink-0">{time}</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="min-w-0 flex-1 truncate">{event.summary}</span>
          {!event.allDay && (
            <span className="shrink-0 opacity-70">
              {formatReminderEstimate(calendarEventMinutes(event))}
            </span>
          )}
        </span>
      </div>
    </HoverCard>
  );
}

/**
 * An approved absence, on one of the days it covers. The server flattens a
 * request into one `LeaveDay` per calendar day (weekends included), so the
 * chip knows nothing about spans: it simply repeats in every cell, and that
 * repetition is what makes a week off read as a band across the grid.
 *
 * Because of that there is nothing per-day to open — a day of leave is not a
 * record of its own — so this is a plain div like the Google event chip, with
 * the whole absence in its tooltip.
 *
 * The range is spelled out even in a single cell: the useful thing about
 * seeing "Tuesday: Maria away" is knowing she is away until Friday.
 */
export function LeaveChip({
  leave,
  locale,
  showAssignee = true,
}: {
  leave: LeaveDay;
  locale: string;
  showAssignee?: boolean;
}) {
  const { t } = useI18n();
  const typeLabel = leaveTypeLabel(leave.type);
  // The year is dropped while the absence sits inside the year of the cell it
  // is painted in — the calendar header already says which year that is.
  const range = formatLeaveRange(
    leave.startDate,
    leave.endDate,
    locale,
    Number(leave.day.slice(0, 4)),
  );
  return (
    <div
      className={chipClass('cursor-default')}
      style={statusBadgeStyle(LEAVE_TYPE_COLOR[leave.type])}
      title={`${t('leave.calendarChip', { name: leave.userName, type: typeLabel })} · ${range}`}
    >
      <span className="flex items-center gap-1 text-[10px] font-normal leading-tight opacity-75">
        <Palmtree size={10} className="shrink-0" />
        <span className="min-w-0 truncate">{typeLabel}</span>
      </span>
      {showAssignee ? (
        <span className="flex items-center gap-1">
          <span className="min-w-0 flex-1 truncate">{leave.userName}</span>
          <span className="shrink-0 opacity-70">{range}</span>
        </span>
      ) : (
        // Filtered to one person, the name is already in the toolbar and would
        // only crowd out the one thing the cell can still tell you.
        <span className="block truncate">{range}</span>
      )}
    </div>
  );
}

/**
 * A finished meeting opens its transcript; a coming one opens the room (its
 * lobby, until the link opens). A cancelled one is greyed and struck through.
 */
export function MeetingChip({ meeting, locale }: { meeting: VideoMeetingSummary; locale: string }) {
  const navigate = useNavigate();
  const { t } = useI18n();
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(
    new Date(meeting.scheduledAt),
  );
  const past = meeting.status === 'ENDED' || meeting.status === 'CANCELLED';
  const cancelled = meeting.status === 'CANCELLED';
  return (
    <HoverCard content={<MeetingPreviewCard meeting={meeting} />}>
      <button
        type="button"
        onClick={() =>
          navigate(
            past
              ? `/meet/history/${encodeURIComponent(meeting.id)}`
              : `/meet/${encodeURIComponent(meeting.code)}`,
          )
        }
        className={chipClass(cancelled ? 'line-through' : '')}
        style={statusBadgeStyle(meetingColor(meeting))}
      >
        <span className="flex items-center gap-1 text-[10px] font-normal leading-tight opacity-75">
          <Video size={10} className="shrink-0" />
          <span className="min-w-0 truncate">{t('meet.title')}</span>
          <span className="shrink-0">·</span>
          <span className="min-w-0 truncate">{meetingStatusLabel(meeting.status, t)}</span>
        </span>
        <span className="flex items-center gap-1">
          <span className="min-w-0 flex-1 truncate">
            {time} {meeting.title}
          </span>
          <span className="shrink-0 opacity-70">
            {formatReminderEstimate(meeting.durationMinutes)}
          </span>
        </span>
      </button>
    </HoverCard>
  );
}

/**
 * The assignee, as it appears inside a chip: a tiny initials avatar plus the
 * name, kept small enough not to compete with the title. Only rendered while
 * the calendar shows everyone's tasks — with an assignee filter on, every
 * chip would repeat the same person.
 *
 * The name is phone-only: there the agendas give a chip the full width, while
 * on a desktop the chip sits in one of seven ~190px columns where the name
 * would crush the project and status to a letter each. The avatar carries the
 * name as its tooltip and the hover card spells it out.
 */
export function ChipAssignee({ name, size = 12 }: { name: string; size?: number }) {
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1">
      <Avatar name={name} size={size} />
      <span className="max-w-[96px] truncate md:hidden">{name}</span>
    </span>
  );
}

/**
 * A task chip with two lines: the project and status it belongs to (and, when
 * asked, whose task it is), then the priority glyph, the title and its
 * estimate — enough to plan a week without opening anything. Clicking opens
 * the task, hovering previews it.
 *
 * The priority leads the title line (Andrei, 2026-10-04) the way it leads
 * every other task row in the app, and the week column is sorted by it, so a
 * day reads urgent-first straight down. It is the one mark drawn in its own
 * colour rather than the status tint — that is what makes it readable at a
 * glance against a column of same-coloured chips.
 *
 * Both halves truncate because in a seven-column grid neither is guaranteed
 * room; in the mobile agendas the chip spans the full width and nothing is cut.
 */
export function TaskChip({ task, showAssignee = false }: { task: Task; showAssignee?: boolean }) {
  const navigate = useNavigate();
  return (
    <TaskHoverCard task={task}>
      <button
        type="button"
        onClick={() => navigate(taskPath(task))}
        className="block w-full rounded px-1.5 py-1 text-left text-xs font-medium hover:opacity-80"
        style={statusBadgeStyle(task.statusColor)}
      >
        {/* Project · status, dimmed so the title stays the thing the eye lands on. */}
        <span className="flex items-center gap-1 text-[10px] font-normal leading-tight opacity-75">
          {task.projectName && (
            <>
              <span className="min-w-0 truncate">{task.projectName}</span>
              <span className="shrink-0">·</span>
            </>
          )}
          <span className="min-w-0 truncate">{task.statusLabel}</span>
          {showAssignee && task.assignee && <ChipAssignee name={task.assignee.name} />}
        </span>
        <span className="flex items-center gap-1">
          <span
            className="flex h-3.5 w-3.5 shrink-0 items-center justify-center"
            title={PRIORITY_LABEL[task.priority]}
            aria-label={PRIORITY_LABEL[task.priority]}
          >
            <PriorityIcon priority={task.priority} size={14} />
          </span>
          <span className="min-w-0 flex-1 truncate">{task.title}</span>
          {task.estimate != null && (
            <span className="shrink-0 opacity-70">{formatEstimate(task.estimate)}</span>
          )}
        </span>
      </button>
    </TaskHoverCard>
  );
}
